import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { SOUNDTRACKS } from "./useSoundtracks";

// Create context for global music settings
const MusicContext = createContext(null);

// Music Provider component - handles background music globally
export function MusicProvider({ children }) {
  const audioContextRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('bananaBlast_musicVolume');
    return saved ? parseFloat(saved) : 0.15;
  });
  const [soundEffectsEnabled, setSoundEffectsEnabled] = useState(() => {
    const saved = localStorage.getItem('bananaBlast_soundEffects');
    return saved !== 'false';
  });
  const [musicEnabled, setMusicEnabled] = useState(() => {
    const saved = localStorage.getItem('bananaBlast_musicEnabled');
    return saved !== 'false';
  });
  const [currentSoundtrackId, setCurrentSoundtrackId] = useState(() => {
    return localStorage.getItem('bananaBlast_selectedSoundtrack') || 'woody';
  });
  const gainNodeRef = useRef(null);
  const intervalRef = useRef(null);
  const hasInteractedRef = useRef(false);
  const shouldBePlayingRef = useRef(false);

  // Get current soundtrack
  const currentSoundtrack = SOUNDTRACKS[currentSoundtrackId] || SOUNDTRACKS.woody;
  const melodyNotes = useRef(currentSoundtrack.melody);
  const bassNotes = useRef(currentSoundtrack.bass);
  const tempoRef = useRef(currentSoundtrack.tempo);

  // Update soundtrack when it changes
  const setSoundtrack = useCallback((soundtrackId) => {
    const soundtrack = SOUNDTRACKS[soundtrackId];
    if (soundtrack) {
      setCurrentSoundtrackId(soundtrackId);
      localStorage.setItem('bananaBlast_selectedSoundtrack', soundtrackId);
      melodyNotes.current = soundtrack.melody;
      bassNotes.current = soundtrack.bass;
      tempoRef.current = soundtrack.tempo;
      
      // Restart music with new soundtrack if playing
      if (shouldBePlayingRef.current && intervalRef.current) {
        stopMusicInternal();
        setTimeout(() => startMusicInternal(), 100);
      }
    }
  }, []);

  // Initialize audio context
  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.connect(audioContextRef.current.destination);
      gainNodeRef.current.gain.value = volume;
    }
    return audioContextRef.current;
  }, [volume]);

  const playNote = useCallback((freq, duration, type = 'sine', vol = 0.1) => {
    const ctx = audioContextRef.current;
    if (!ctx || !gainNodeRef.current) return;

    try {
      const osc = ctx.createOscillator();
      const noteGain = ctx.createGain();

      osc.connect(noteGain);
      noteGain.connect(gainNodeRef.current);

      osc.type = type;
      osc.frequency.value = freq;

      noteGain.gain.setValueAtTime(vol, ctx.currentTime);
      noteGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + duration + 0.1);
    } catch (e) {
      // Ignore audio errors
    }
  }, []);

  const stopMusicInternal = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  const startMusicInternal = useCallback(() => {
    // Don't start if already playing or music is disabled
    if (intervalRef.current || !musicEnabled) return;
    
    const ctx = initAudio();
    if (!ctx) return;

    // Resume context if suspended (browser policy)
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    setIsPlaying(true);
    shouldBePlayingRef.current = true;

    let melodyIndex = 0;
    let bassIndex = 0;
    let beatCount = 0;

    const playBeat = () => {
      if (!audioContextRef.current || !shouldBePlayingRef.current) return;

      // Check if context is still running
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
        return;
      }

      // Play melody note
      const melody = melodyNotes.current;
      const bass = bassNotes.current;
      
      if (melody && melody.length > 0) {
        const melodyNote = melody[melodyIndex % melody.length];
        playNote(melodyNote.freq, melodyNote.dur, 'sine', 0.08);
        melodyIndex++;
      }

      // Play bass every 2 beats
      if (beatCount % 2 === 0 && bass && bass.length > 0) {
        const bassNote = bass[bassIndex % bass.length];
        playNote(bassNote.freq, bassNote.dur, 'triangle', 0.06);
        bassIndex++;
      }

      // Add occasional percussion (high pitched tick)
      if (beatCount % 4 === 0) {
        playNote(1200, 0.05, 'square', 0.03);
      }

      beatCount++;
    };

    // Play at tempo from current soundtrack
    intervalRef.current = setInterval(playBeat, tempoRef.current);
    playBeat(); // Start immediately
  }, [initAudio, playNote, musicEnabled]);

  const startMusic = useCallback(() => {
    shouldBePlayingRef.current = true;
    // Reset volume to normal when starting
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = volume;
    }
    startMusicInternal();
  }, [startMusicInternal, volume]);

  const stopMusic = useCallback(() => {
    shouldBePlayingRef.current = false;
    stopMusicInternal();
  }, [stopMusicInternal]);

  // Fade out music over duration (in ms)
  const fadeOutMusic = useCallback((duration = 1000) => {
    if (!gainNodeRef.current || !audioContextRef.current) {
      stopMusic();
      return;
    }

    shouldBePlayingRef.current = false;
    const ctx = audioContextRef.current;
    const currentVolume = gainNodeRef.current.gain.value;
    
    // Exponential fade out
    gainNodeRef.current.gain.setValueAtTime(currentVolume, ctx.currentTime);
    gainNodeRef.current.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration / 1000);
    
    // Stop the interval after fade completes
    setTimeout(() => {
      stopMusicInternal();
      // Reset gain for next time
      if (gainNodeRef.current) {
        gainNodeRef.current.gain.value = volume;
      }
    }, duration);
  }, [stopMusicInternal, stopMusic, volume]);

  // Fade in music over duration (in ms)
  const fadeInMusic = useCallback((duration = 1000) => {
    if (!hasInteractedRef.current || !musicEnabled) return;
    
    shouldBePlayingRef.current = true;
    const ctx = initAudio();
    if (!ctx) return;
    
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    
    // Start at very low volume
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.setValueAtTime(0.001, ctx.currentTime);
      gainNodeRef.current.gain.exponentialRampToValueAtTime(volume, ctx.currentTime + duration / 1000);
    }
    
    startMusicInternal();
  }, [initAudio, startMusicInternal, musicEnabled, volume]);

  const toggleMusic = useCallback(() => {
    const newEnabled = !musicEnabled;
    setMusicEnabled(newEnabled);
    localStorage.setItem('bananaBlast_musicEnabled', newEnabled.toString());
    
    if (newEnabled && hasInteractedRef.current) {
      startMusic();
    } else {
      stopMusic();
    }
  }, [musicEnabled, startMusic, stopMusic]);

  const setMusicVolume = useCallback((newVolume) => {
    setVolume(newVolume);
    localStorage.setItem('bananaBlast_musicVolume', newVolume.toString());
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = newVolume;
    }
  }, []);

  const toggleSoundEffects = useCallback(() => {
    const newEnabled = !soundEffectsEnabled;
    setSoundEffectsEnabled(newEnabled);
    localStorage.setItem('bananaBlast_soundEffects', newEnabled.toString());
  }, [soundEffectsEnabled]);

  // Auto-start music on first user interaction
  useEffect(() => {
    const handleFirstInteraction = () => {
      if (!hasInteractedRef.current) {
        hasInteractedRef.current = true;
        if (musicEnabled) {
          // Small delay to ensure audio context is ready
          setTimeout(() => {
            startMusic();
          }, 100);
        }
      }
    };

    window.addEventListener('click', handleFirstInteraction);
    window.addEventListener('touchstart', handleFirstInteraction);
    window.addEventListener('keydown', handleFirstInteraction);

    return () => {
      window.removeEventListener('click', handleFirstInteraction);
      window.removeEventListener('touchstart', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
    };
  }, [musicEnabled, startMusic]);

  // Handle visibility change - restart music when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && shouldBePlayingRef.current && musicEnabled) {
        // Resume audio context and restart music if needed
        if (audioContextRef.current?.state === 'suspended') {
          audioContextRef.current.resume().catch(() => {});
        }
        if (!intervalRef.current) {
          startMusicInternal();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [musicEnabled, startMusicInternal]);

  // Keep music playing - check every second
  useEffect(() => {
    const checkInterval = setInterval(() => {
      if (shouldBePlayingRef.current && musicEnabled && hasInteractedRef.current) {
        // Restart if interval got cleared but should be playing
        if (!intervalRef.current) {
          startMusicInternal();
        }
        // Resume audio context if suspended
        if (audioContextRef.current?.state === 'suspended') {
          audioContextRef.current.resume().catch(() => {});
        }
      }
    }, 1000);

    return () => clearInterval(checkInterval);
  }, [musicEnabled, startMusicInternal]);

  // Cleanup only on full unmount (app close)
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // Don't close audio context - let browser handle it
    };
  }, []);

  const value = {
    isPlaying,
    musicEnabled,
    soundEffectsEnabled,
    volume,
    currentSoundtrackId,
    startMusic,
    stopMusic,
    fadeOutMusic,
    fadeInMusic,
    toggleMusic,
    toggleSoundEffects,
    setMusicVolume,
    setSoundtrack,
  };

  return (
    <MusicContext.Provider value={value}>
      {children}
    </MusicContext.Provider>
  );
}

// Hook to use music settings
export function useMusicSettings() {
  const context = useContext(MusicContext);
  if (!context) {
    throw new Error('useMusicSettings must be used within a MusicProvider');
  }
  return context;
}
